import React, {ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState} from "react"
import { Icon, Input } from "semantic-ui-react"
import {Place} from "../model/autocomplete-place"


interface PlaceColumnProps {
    maxLength: number
    onChange: (newValue?: string) => void
    autocomplete?: boolean // new optional prop
    value?: string
    inputClassname?: string
    popperClassname?: string
}

function useDebouncedValue<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debounced
}

const autocompleteUrl = (place: string, limit = 10) =>
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place)}&format=json&addressdetails=1&limit=${limit}`

export const CreatorLocationCell = ({
    maxLength,
    value,
    inputClassname,
    popperClassname,
    onChange,
    autocomplete = false
}: PlaceColumnProps) => {
    const [pendingValue, setPendingValue] = useState(value ?? "")
    const debouncedValue = useDebouncedValue(pendingValue, 300)
    const inputRef = useRef<HTMLInputElement>()
    const popperRef = useRef<HTMLUListElement | null>(null)
    const abortRef = useRef<AbortController | null>(null)
    const [suggestions, setSuggestions] = useState<Place[]>([])
    const [isFocused, setIsFocused] = useState(false)
    const [activeIndex, setActiveIndex] = useState(-1)
    const [popperDirection, setPopperDirection] = useState<"downwards" | "upwards">("downwards")

    /**
     * Popper horizontal positioning.
     */
    useEffect(() => {
        if (!autocomplete
            || !popperClassname?.includes("until-end")
            || !inputRef.current || !popperRef.current)
            return
        const observer = new ResizeObserver(() => {
            const dateColumn = inputRef.current!.parentElement!.parentElement!
            const dateColumnProperties = window.getComputedStyle(dateColumn)
            const ulProperties = window.getComputedStyle(popperRef.current!)
            const dateColumnWidth = parseFloat(dateColumnProperties.width)
            const ulWidth = parseFloat(ulProperties.width)
            popperRef.current!.style.left = `-${ulWidth - dateColumnWidth}px`
        })
        observer.observe(popperRef.current)
        observer.observe(inputRef.current.parentElement!.parentElement!)
        return () => observer.disconnect()
    }, [autocomplete, popperClassname])

    /**
     * Popper vertical positioning.
     */
    useEffect(() => {
        if (!autocomplete || !isFocused || !inputRef.current) return
        if (inputRef.current.getBoundingClientRect().bottom > window.innerHeight / 2)
            setPopperDirection("upwards")
        else
            setPopperDirection("downwards")
    }, [isFocused, pendingValue, autocomplete])

    /**
     * Autocomplete fetch.
     */
    useEffect(() => {
        if (!autocomplete || !debouncedValue) {
            setSuggestions([])
            return
        }
        if (abortRef.current) abortRef.current.abort()
        abortRef.current = new AbortController()
        fetch(autocompleteUrl(debouncedValue), { signal: abortRef.current.signal })
            .then(res => (res.ok ? res.json() : []))
            .then((results: Place[]) => setSuggestions(dedupPlaces(results)))
            .catch(e => { if (e.name !== "AbortError") console.error("Fetch error:", e) })
    }, [debouncedValue, autocomplete])

    const dedupPlaces = (places: Place[]): Place[] => {
        const seen = new Map<string, Place>()
        return places.reduce<Place[]>((acc, place) => {
            if (!seen.has(place.display_name)) {
                seen.set(place.display_name, place)
                acc.push(place)
            }
            return acc
        }, [])
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!autocomplete || !suggestions.length) {
            if (e.key === "Enter")
                commitValue(pendingValue)
            return
        }
        if (e.key === "ArrowDown") {
            e.preventDefault()
            setActiveIndex(prev => (prev + 1) % suggestions.length)
        } else if (e.key === "ArrowUp") {
            e.preventDefault()
            setActiveIndex(prev => (prev - 1 + suggestions.length) % suggestions.length)
        } else if (e.key === "Enter") {
            if (activeIndex >= 0) {
                const s = suggestions[activeIndex]
                commitValue(`${s.name}, ${s.address.country}`)
            } else
                commitValue(pendingValue)
        } else if (e.key === "Escape") {
            e.preventDefault()
            setSuggestions([])
            setActiveIndex(-1)
        }
    }

    const commitValue = useCallback((place: string = "") => {
        setPendingValue(place)
        if (place !== (value ?? "")) onChange(place || undefined)
        setSuggestions([])
        setActiveIndex(-1)
    }, [onChange, value])

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setPendingValue(e.target.value)
        setActiveIndex(-1)
    }

    const suggestionsList = useMemo(() => {
        if (!suggestions.length) return
        return (
            <ul className={`place-suggestions ${popperClassname} ${popperDirection === "upwards" ? "upwards" : "downwards"}`} ref={popperRef}>
                {suggestions.map((s, idx) => (
                    <li key={s.place_id} className={idx === activeIndex ? "highlighted" : ""} onClick={() => commitValue(`${s.name}, ${s.address.country}`)}>
                        <Icon name="map marker alternate" size="small"/>&nbsp;{s.display_name}
                    </li>
                ))}
            </ul>
        )
    }, [suggestions, activeIndex, popperDirection, commitValue, popperClassname])

    return (
        <>
            <Input
                input={{ ref: inputRef }}
                maxLength={maxLength}
                value={pendingValue}
                className={inputClassname || undefined}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                    commitValue(pendingValue)
                    setTimeout(() => setIsFocused(false), 150)
                }}
            />
            {autocomplete && isFocused && suggestionsList}
        </>
    )
}